/**
 * @file    mockFiltersOptions.ts
 * @scope   SHARED
 * @summary Derives filter operator options from an AdvancedFilterFieldConfig for the filter editors.
 * @consumes AdvancedFilterFieldConfig
 * @produces `FilterOperatorOption`, `getOperatorsForField`
 * @sideEffects None — pure derivation.
 * @notes The API returns OperatorOptions as Record<FriendlyName, OperatorSymbol> (e.g.
 *        { "Is Exactly": "=", "Any of these": "IN" }); this flips it into { value, label } entries.
 */
import { AdvancedFilterFieldConfig } from 'models/WidgetModel';

/**
 * Operator option derived from an AdvancedFilterFieldConfig.OperatorOptions entry.
 * `value` is the operator symbol (e.g. "=", "IN", ">") and `label` is the
 * human-readable name (e.g. "Is Exactly", "Any of these", "Greater Than").
 */
export interface FilterOperatorOption {
  value: string;
  label: string;
}

/**
 * Derives the list of operator options from an AdvancedFilterFieldConfig.
 * The API returns OperatorOptions as a Record<FriendlyName, OperatorSymbol>
 * (e.g. { "Is Exactly": "=", "Any of these": "IN" }).
 */
export const getOperatorsForField = (field?: AdvancedFilterFieldConfig): FilterOperatorOption[] => {
  if (!field?.operatorOptions) return [];
  return Object.entries(field.operatorOptions).map(([label, value]) => ({
    value,
    label,
  }));
};
